import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
  $createParagraphNode,
  $createTextNode,
} from "lexical";
import {
  INSERT_TABLE_COMMAND,
  $isTableCellNode,
  $createTableCellNode,
  $createTableRowNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowNodeFromTableCellNodeOrThrow,
  TableCellNode,
  TableRowNode,
  TableNode,
} from "@lexical/table";
import { TOGGLE_LINK_COMMAND, $isLinkNode, LinkNode } from "@lexical/link";
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from "@lexical/list";
import { $createHeadingNode, HeadingTagType } from "@lexical/rich-text";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { Icon } from "@/components/icon/icon";
import Stack from "@/components/stack/stack";

const ToolbarPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isHighlight, setIsHighlight] = useState(false);
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);

  const [isLink, setIsLink] = useState(false);
  const [currentLinkUrl, setCurrentLinkUrl] = useState("");
  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [linkInput, setLinkInput] = useState("");

  const linkDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showLinkEditor) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!linkDropdownRef.current) return;
      if (!linkDropdownRef.current.contains(e.target as Node)) {
        setShowLinkEditor(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showLinkEditor]);

  const [tableNode, setTableNode] = useState<TableNode | null>(null);
  const [tableRowNode, setTableRowNode] = useState<TableRowNode | null>(null);
  const [tableCellNode, setTableCellNode] = useState<TableCellNode | null>(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsHighlight(selection.hasFormat("highlight"));

      // link state
      let node = selection.anchor.getNode();
      let foundLink: LinkNode | null = null;
      while (node) {
        if ($isLinkNode(node)) {
          foundLink = node as LinkNode;
          break;
        }
        const parent = node.getParent();
        if (!parent) break;
        node = parent;
      }
      if (foundLink) {
        setIsLink(true);
        try {
          const url = foundLink.getURL();
          setCurrentLinkUrl(url || "");
        } catch {
          setCurrentLinkUrl("");
        }
      } else {
        setIsLink(false);
        setCurrentLinkUrl("");
      }

      // table state
      let currentNode = selection.anchor.getNode();
      let foundCell: TableCellNode | null = null;

      while (currentNode) {
        if ($isTableCellNode(currentNode)) {
          foundCell = currentNode;
          break;
        }
        const parentNode = currentNode.getParent();
        if (!parentNode) break;
        currentNode = parentNode;
      }

      if (foundCell) {
        const row = $getTableRowNodeFromTableCellNodeOrThrow(foundCell);
        const table = $getTableNodeFromLexicalNodeOrThrow(row);
        setTableCellNode(foundCell);
        setTableRowNode(row);
        setTableNode(table);
      } else {
        setTableCellNode(null);
        setTableRowNode(null);
        setTableNode(null);
      }
    } else {
      setTableCellNode(null);
      setTableRowNode(null);
      setTableNode(null);
      setIsLink(false);
      setCurrentLinkUrl("");
    }
  }, []);

  React.useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        // do not auto-close the link editor on selection change
        return false;
      },
      1,
    );
  }, [editor, updateToolbar]);

  const formatText = useCallback(
    (format: TextFormatType) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [editor],
  );

  const insertTable = useCallback(() => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: "3",
      rows: "3",
      includeHeaders: {
        rows: true,
        columns: false,
      },
    });
  }, [editor]);

  const insertHeading = useCallback(
    (headingLevel: HeadingTagType) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const headingNode = $createHeadingNode(headingLevel);
          // Extract the selected text content
          const textContent = selection.getTextContent();
          // If there's selected text, add it to the heading node
          if (textContent) {
            const textNode = $createTextNode(textContent);
            headingNode.append(textNode);
          }
          selection.insertNodes([headingNode]);
        }
      });
      setShowHeadingDropdown(false);
    },
    [editor],
  );

  const insertList = useCallback(
    (type: "bullet" | "number") => {
      if (type === "bullet") {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      } else {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      }
    },
    [editor],
  );

  const openLinkEditor = useCallback(() => {
    setLinkInput(currentLinkUrl || "");
    setShowLinkEditor((v) => !v);
  }, [currentLinkUrl]);

  const applyLink = useCallback(() => {
    const value = linkInput.trim();
    if (!value) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      // URL normalization: add https:// if missing scheme
      const normalized =
        /^mailto:/i.test(value) || /^(https?:)?\/\//i.test(value)
          ? value
          : `https://${value}`;
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalized);
    }
    setShowLinkEditor(false);
  }, [editor, linkInput]);

  const removeLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setShowLinkEditor(false);
  }, [editor]);

  const insertRowAbove = useCallback(() => {
    if (!tableNode || !tableRowNode) return;
    editor.update(() => {
      const tableRows = tableNode.getChildren() as TableRowNode[];
      const currentRowIndex = tableRows.findIndex((row) => row.is(tableRowNode));

      if (currentRowIndex >= 0) {
        const cellsCount = tableRowNode.getChildren().length;
        const newRow = $createTableRowNode();
        const isHeaderRow = currentRowIndex === 0;

        for (let i = 0; i < cellsCount; i++) {
          const newCell = $createTableCellNode(isHeaderRow ? 1 : 0);
          const paragraph = $createParagraphNode();
          newCell.append(paragraph);
          newRow.append(newCell);
        }

        if (currentRowIndex === 0) {
          tableNode.getFirstChild()?.insertBefore(newRow);
        } else {
          tableRows[currentRowIndex - 1].insertAfter(newRow);
        }
      }
    });
  }, [editor, tableNode, tableRowNode]);

  const insertRowBelow = useCallback(() => {
    if (!tableRowNode || !tableNode) return;
    editor.update(() => {
      const cellsCount = tableRowNode.getChildren().length;
      const newRow = $createTableRowNode();

      for (let i = 0; i < cellsCount; i++) {
        const newCell = $createTableCellNode(0);
        const paragraph = $createParagraphNode();
        newCell.append(paragraph);
        newRow.append(newCell);
      }

      tableRowNode.insertAfter(newRow);
    });
  }, [editor, tableRowNode, tableNode]);

  const insertColumnLeft = useCallback(() => {
    if (!tableNode || !tableRowNode || !tableCellNode) return;
    editor.update(() => {
      const tableRows = tableNode.getChildren() as TableRowNode[];
      const currentCells = tableRowNode.getChildren() as TableCellNode[];
      const currentColumnIndex = currentCells.findIndex((cell) => cell.is(tableCellNode));

      if (currentColumnIndex >= 0) {
        tableRows.forEach((row, rowIndex) => {
          const cells = row.getChildren() as TableCellNode[];
          const isHeaderRow = rowIndex === 0;
          const newCell = $createTableCellNode(isHeaderRow ? 1 : 0);
          const paragraph = $createParagraphNode();
          newCell.append(paragraph);

          if (currentColumnIndex === 0) {
            row.getFirstChild()?.insertBefore(newCell);
          } else {
            cells[currentColumnIndex - 1].insertAfter(newCell);
          }
        });
      }
    });
  }, [editor, tableNode, tableRowNode, tableCellNode]);

  const insertColumnRight = useCallback(() => {
    if (!tableNode || !tableRowNode || !tableCellNode) return;
    editor.update(() => {
      const tableRows = tableNode.getChildren() as TableRowNode[];
      const currentCells = tableRowNode.getChildren() as TableCellNode[];
      const currentColumnIndex = currentCells.findIndex((cell) => cell.is(tableCellNode));

      if (currentColumnIndex >= 0) {
        tableRows.forEach((row, rowIndex) => {
          const cells = row.getChildren() as TableCellNode[];
          const isHeaderRow = rowIndex === 0;
          const newCell = $createTableCellNode(isHeaderRow ? 1 : 0);
          const paragraph = $createParagraphNode();
          newCell.append(paragraph);

          if (cells[currentColumnIndex]) {
            cells[currentColumnIndex].insertAfter(newCell);
          }
        });
      }
    });
  }, [editor, tableNode, tableRowNode, tableCellNode]);

  const deleteRow = useCallback(() => {
    if (!tableNode || !tableRowNode) return;
    editor.update(() => {
      const rows = tableNode.getChildren() as TableRowNode[];
      if (rows.length <= 1) {
        tableNode.remove();
        return;
      }
      tableRowNode.remove();
    });
  }, [editor, tableNode, tableRowNode]);

  const deleteColumn = useCallback(() => {
    if (!tableNode || !tableRowNode || !tableCellNode) return;
    editor.update(() => {
      const rows = tableNode.getChildren() as TableRowNode[];
      const currentCells = tableRowNode.getChildren() as TableCellNode[];
      const columnIndex = currentCells.findIndex((cell) => cell.is(tableCellNode));
      if (columnIndex < 0) return;
      const firstRowCells = rows[0].getChildren() as TableCellNode[];
      if (firstRowCells.length <= 1) {
        tableNode.remove();
        return;
      }
      rows.forEach((row) => {
        const cells = row.getChildren() as TableCellNode[];
        const target = cells[columnIndex];
        if (target) target.remove();
      });
    });
  }, [editor, tableNode, tableRowNode, tableCellNode]);

  return (
    <div className="lexical-toolbar">
      <div className="lexical-toolbar-group">
        <button
          type="button"
          className={`lexical-toolbar-button ${isBold ? "active" : ""}`}
          onClick={() => formatText("bold")}
          title="Bold"
        >
          <Icon name="bold" size={16} />
        </button>
        <button
          type="button"
          className={`lexical-toolbar-button ${isItalic ? "active" : ""}`}
          onClick={() => formatText("italic")}
          title="Italic"
        >
          <Icon name="italic" size={16} />
        </button>
        <button
          type="button"
          className={`lexical-toolbar-button ${isStrikethrough ? "active" : ""}`}
          onClick={() => formatText("strikethrough")}
          title="Strikethrough"
        >
          <Icon name="strikethrough" size={16} />
        </button>
        <button
          type="button"
          className={`lexical-toolbar-button ${isHighlight ? "active" : ""}`}
          onClick={() => formatText("highlight")}
          title="Highlight"
        >
          <Icon name="highlighter" size={16} />
        </button>
        <button
          type="button"
          className={`lexical-toolbar-button ${isLink ? "active" : ""}`}
          onClick={openLinkEditor}
          title={isLink ? "Edit link" : "Insert link"}
        >
          <Icon name="link" size={16} />
        </button>
        {showLinkEditor && (
          <div className="lexical-toolbar-dropdown-menu" ref={linkDropdownRef}>
            <input
              type="text"
              className="lexical-toolbar-input"
              placeholder="https://example.com"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyLink();
                if (e.key === "Escape") setShowLinkEditor(false);
              }}
            />
            <div className="lexical-toolbar-actions">
              <button
                type="button"
                className="lexical-toolbar-dropdown-item"
                onClick={applyLink}
              >
                Apply
              </button>
              {isLink && (
                <button
                  type="button"
                  className="lexical-toolbar-dropdown-item"
                  onClick={removeLink}
                >
                  Unlink
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="lexical-toolbar-separator" />

      <div className="lexical-toolbar-group">
        <div className="lexical-toolbar-dropdown">
          <button
            type="button"
            className="lexical-toolbar-button"
            onClick={() => setShowHeadingDropdown(!showHeadingDropdown)}
            title="Heading"
          >
            <Icon name="heading" size={16} />
          </button>
          {showHeadingDropdown && (
            <div className="lexical-toolbar-dropdown-menu">
              <button
                type="button"
                onClick={() => insertHeading("h1")}
                className="lexical-toolbar-dropdown-item"
              >
                H1
              </button>
              <button
                type="button"
                onClick={() => insertHeading("h2")}
                className="lexical-toolbar-dropdown-item"
              >
                H2
              </button>
              <button
                type="button"
                onClick={() => insertHeading("h3")}
                className="lexical-toolbar-dropdown-item"
              >
                H3
              </button>
              <button
                type="button"
                onClick={() => insertHeading("h4")}
                className="lexical-toolbar-dropdown-item"
              >
                H4
              </button>
              <button
                type="button"
                onClick={() => insertHeading("h5")}
                className="lexical-toolbar-dropdown-item"
              >
                H5
              </button>
              <button
                type="button"
                onClick={() => insertHeading("h6")}
                className="lexical-toolbar-dropdown-item"
              >
                H6
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="lexical-toolbar-separator" />

      <div className="lexical-toolbar-group">
        <button
          type="button"
          className="lexical-toolbar-button"
          onClick={() => insertList("bullet")}
          title="Bullet List"
        >
          <Icon name="list" size={16} />
        </button>
        <button
          type="button"
          className="lexical-toolbar-button"
          onClick={() => insertList("number")}
          title="Numbered List"
        >
          <Icon name="list-ordered" size={16} />
        </button>
      </div>

      <div className="lexical-toolbar-separator" />

      <div className="lexical-toolbar-group">
        {!tableNode && (
          <button
            type="button"
            className="lexical-toolbar-button"
            onClick={insertTable}
            title="Insert Table"
          >
            <Icon name="table" size={16} />
          </button>
        )}
        {tableNode && (
          <>
            <button
              type="button"
              className="lexical-toolbar-button"
              onClick={insertRowAbove}
              title="Insert row above"
            >
              <Stack>
                <Icon name="arrow-up-from-line" size={16} />
                <span>row</span>
              </Stack>
            </button>
            <button
              type="button"
              className="lexical-toolbar-button"
              onClick={insertRowBelow}
              title="Insert row below"
            >
              <Stack>
                <Icon name="arrow-down-from-line" size={16} />
                row
              </Stack>
            </button>
            <button
              type="button"
              className="lexical-toolbar-button"
              onClick={insertColumnLeft}
              title="Insert column left"
            >
              <Stack>
                <Icon name="arrow-left-from-line" size={16} />
                col
              </Stack>
            </button>
            <button
              type="button"
              className="lexical-toolbar-button"
              onClick={insertColumnRight}
              title="Insert column right"
            >
              <Stack>
                <Icon name="arrow-right-from-line" size={16} />
                col
              </Stack>
            </button>
            <button
              type="button"
              className="lexical-toolbar-button"
              onClick={deleteRow}
              title="Delete row"
            >
              <Stack>
                <Icon name="x" size={16} />
                row
              </Stack>
            </button>
            <button
              type="button"
              className="lexical-toolbar-button"
              onClick={deleteColumn}
              title="Delete column"
            >
              <Stack>
                <Icon name="x" size={16} />
                col
              </Stack>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ToolbarPlugin;
